import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, Alert, Typography } from 'antd';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import { authService } from '../../services/authService';

import SelecteurLangue from '../../components/SelecteurLangue';

const { Text } = Typography;
const VERT = '#1a6b3c';

/**
 * Réinitialisation en libre-service, en deux étapes sur un seul écran.
 *
 * Le message de l'étape 1 est volontairement le même quelle que soit l'adresse
 * saisie : le dire autrement transformerait cet écran en annuaire des comptes.
 * L'adresse affichée ensuite est celle que le SERVEUR renvoie masquée, jamais
 * celle qu'on vient de taper - sinon l'écran confirmerait la saisie.
 */
export default function MotDePasseOublie() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [etape, setEtape] = useState('DEMANDE');
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur] = useState('');
  const [formDemande] = Form.useForm();
  const [formCode] = Form.useForm();

  const demander = async ({ email }) => {
    setLoading(true);
    setErreur('');
    try {
      setSession(await authService.demanderReinitialisation(email));
      setEtape('CODE');
    } catch (e) {
      setErreur(e?.response?.data?.message || t('motDePasseOublie.messages.demandeImpossible'));
    } finally {
      setLoading(false);
    }
  };

  const reinitialiser = async ({ code, motDePasse, confirmation }) => {
    if (motDePasse !== confirmation) {
      setErreur(t('motDePasseOublie.messages.nonIdentiques'));
      return;
    }
    setLoading(true);
    setErreur('');
    try {
      await authService.reinitialiserMotDePasse(session.otpTrackingId, code, motDePasse);
      toast.success(t('motDePasseOublie.messages.modifie'));
      navigate('/login', { replace: true });
    } catch (e) {
      setErreur(e?.response?.data?.message || t('motDePasseOublie.messages.codeIncorrect'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: 'calc(100vh - var(--cb-banniere-h, 0px))', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#f4faf7', padding: 16,
    }}>
      <SelecteurLangue variante="clair" flottant />
      <div style={{
        background: '#fff', borderRadius: 16, padding: '40px 32px',
        width: '100%', maxWidth: 420, boxShadow: '0 12px 40px rgba(15,69,37,.12)',
      }}>
        <h1 style={{ fontSize: '1.3rem', fontWeight: 700, color: VERT, marginBottom: 8 }}>
          {t('motDePasseOublie.titre')}
        </h1>

        {erreur && (
          <Alert type="error" message={erreur} showIcon closable
            onClose={() => setErreur('')} style={{ marginBottom: 16 }} />
        )}

        {etape === 'DEMANDE' ? (
          <>
            <Text type="secondary" style={{ fontSize: 13.5, display: 'block', marginBottom: 20 }}>
              {t('motDePasseOublie.demande.intro')}
            </Text>
            <Form form={formDemande} layout="vertical" onFinish={demander}>
              <Form.Item name="email" label={t('motDePasseOublie.demande.adresseEmail')}
                rules={[{ required: true, type: 'email', message: t('motDePasseOublie.demande.adresseInvalide') }]}>
                <Input size="large" autoComplete="email" autoFocus />
              </Form.Item>
              <Button type="primary" htmlType="submit" block size="large"
                loading={loading} style={{ background: VERT }}>
                {t('motDePasseOublie.demande.recevoirCode')}
              </Button>
            </Form>
          </>
        ) : (
          <>
            <Text type="secondary" style={{ fontSize: 13.5, display: 'block', marginBottom: 20 }}>
              <Trans t={t} i18nKey="motDePasseOublie.code.intro" components={{ 1:
              <Text strong /> }}
                     values={{ email: session?.emailMasque, minutes: session?.validiteMinutes }} />
            </Text>
            <Form form={formCode} layout="vertical" onFinish={reinitialiser}>
              <Form.Item name="code" label={t('motDePasseOublie.code.codeRecu')}
                rules={[{ required: true, pattern: /^\d{6}$/, message: t('motDePasseOublie.code.sixChiffres') }]}>
                <Input size="large" inputMode="numeric" maxLength={6} autoFocus />
              </Form.Item>
              <Form.Item name="motDePasse" label={t('motDePasseOublie.code.nouveauMotDePasse')}
                rules={[{ required: true, message: t('motDePasseOublie.code.obligatoire') }]}>
                <Input.Password size="large" autoComplete="new-password" />
              </Form.Item>
              <Form.Item name="confirmation" label={t('motDePasseOublie.code.confirmer')}
                rules={[{ required: true, message: t('motDePasseOublie.code.obligatoire') }]}>
                <Input.Password size="large" autoComplete="new-password" />
              </Form.Item>
              <Button type="primary" htmlType="submit" block size="large"
                loading={loading} style={{ background: VERT }}>
                {t('motDePasseOublie.code.changer')}
              </Button>
            </Form>
          </>
        )}

        <div style={{ textAlign: 'center', marginTop: 18 }}>
          <Button type="link" size="small" onClick={() => navigate('/login')}
            style={{ color: '#5a7a66' }}>
            {t('motDePasseOublie.retourConnexion')}
          </Button>
        </div>
      </div>
    </div>
  );
}
